import React, {useEffect, useState} from 'react';
import {getUserFriendlyErrorMessage} from '@/utils/errorHandling';
import {
  Button,
  EmptyState,
  ErrorHappened,
  HeadingSize,
  Label,
  LabelType,
  Loading,
  NoResultsFound,
  Search,
  SorterResult,
  Table,
  useKeyboardShortcut
} from '@fs/uikit-react';
import styles from './Settings.module.css';
import {
  filteredSettings,
  getRowActions,
  getTableColumns,
  handleCreateSetting,
  handleDeleteSetting,
  handleEditSetting
} from "@/pages/SystemAdmin/Settings/_private/table.tsx";
import {Setting} from './SettingsApi';
import {
  refreshSettings,
  useInit
} from "@/pages/SystemAdmin/Settings/_private/init.ts";

const SettingsPage: React.FC = () => {
  const [searchValue, setSearchValue] = useState('');

  const [settings, setSettings] = useState<Setting[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<unknown>(null);
  const [sorters, setSorters] = useState<SorterResult[]>([
    {columnKey: 'scope', order: 'ascend'},
    {columnKey: 'key', order: 'ascend'}
  ]);

  useInit(setSettings, setLoading, setError);

  useEffect(() => {
    refreshSettings();
  }, []);

  // Use keyboard shortcut hook for 'c' key to create setting
  useKeyboardShortcut('c', [], handleCreateSetting);

  const filtered = filteredSettings(settings, searchValue);

  const renderHeader = () => {
    return (
      <div className={styles.title}>
        <Label
          text={'Налаштування'}
          type={LabelType.HEADING}
          headingSize={HeadingSize.L}
        />
        <div className={styles.buttonContainer}>
          <Button
            onClick={handleCreateSetting}
            title="Створити налаштування"
          />
        </div>
      </div>
    );
  };

  const renderSearch = () => {
    return (
      <div className={styles.searchButtonContainer}>
        <div className={styles.searchContainer}>
          <Search
            placeholder="Пошук налаштувань"
            value={searchValue}
            onChange={setSearchValue}
          />
        </div>
      </div>
    );
  };

  const renderContent = () => {
    if (loading) {
      return <Loading
        text="Загружаємо налаштування..."
        className={styles.loadingState}
      />;
    }

    if (error) {
      return <ErrorHappened text={getUserFriendlyErrorMessage(error)}/>;
    }

    if (filtered.length === 0) {
      if (searchValue) {
        return <NoResultsFound
          text={`Немає результатів за запитом "${searchValue}"`}
        />;
      } else {
        return <EmptyState text="Немає доступних налаштувань"/>;
      }
    }

    return (
      <Table
        data={filtered || []}
        columns={getTableColumns()}
        rowActions={getRowActions(handleDeleteSetting)}
        onClick={(record) => {
          handleEditSetting(record as Setting);
        }}
        onSort={setSorters}
        initialSorters={sorters}
      />
    );
  };

  return (
    <div className={styles.container}>
      {/* Header */}
      {renderHeader()}

      <div>
        {/* Filters */}
        {renderSearch()}

        {/* Body */}
        {renderContent()}
      </div>
    </div>
  );
};

export default SettingsPage;
